import { $parse } from "@opendash/plugin-parse";
import { ParseFieldSchema } from "@opendash/plugin-parse/dist/types";
import { useWatch } from "antd/es/form/Form";
import { useMemo } from "react";
import { ALLOWED_TYPES } from "../types/Operations";

/**
 * Get parse fields for the selected class, filtered by the selected keys by default
 * @param getAll if true, returns all fields for the selected class and ignores the selected keys
 * @returns the parse fields for the selected class
 */
export const useMappedParseFields = (getAll?: boolean) => {
  const className = useWatch<string>("className");
  const keys = useWatch<string[]>("keys");

  // all valid fields for the selected class
  const fields = useMemo(() => {
    const rawFields = $parse.server.schema.find(
      (c) => c.className === className
    )?.fields;
    if (!rawFields) return [];

    const mappedFields = mapFields(rawFields);

    if (getAll) return mappedFields;

    const validFields = mappedFields.filter(
      (field) =>
        ALLOWED_TYPES.includes(field.type) && keys.includes(field.field)
    );

    return validFields;
  }, [
    $parse.server.schema,
    $parse.server.schema.length,
    className,
    getAll,
    keys,
  ]);

  return fields;
};

function mapFields(
  fields: Record<string, ParseFieldSchema>,
  prefix = ""
): ParseQueryFormFieldType[] {
  return Object.keys(fields).flatMap((field) => {
    const type = fields[field].type.toLocaleLowerCase();
    const targetClass = fields[field].targetClass;

    if (type === "pointer" && prefix === "") {
      const targetFields = $parse.server.schema.find(
        (c) => c.className === targetClass
      )?.fields;
      return [
        {
          field: prefix + field,
          type,
          targetClass,
        },
        ...mapFields(targetFields || {}, field + "."),
      ];
    }
    return [
      {
        field: prefix + field,
        type,
        targetClass,
      },
    ];
  });
}

type ParseQueryFormFieldType = {
  field: string;
  type: string;
  targetClass?: string;
  targetFields?: ParseQueryFormFieldType[];
};
